import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { generateGenogramShapeSVG, type GenogramShapeType } from '../../../lib/genogram/svg-shape-generator';
import { generatePropertiesFromFieldPacks, getGenogramPersonFieldPacks } from '../fields/fieldPackToManifest';
import type { TFunction } from 'i18next';
import { renderMultilineText } from '../../diagram/shapes/svgRenderer.utils';
import type { ComputedCulture, ComputedHealth } from '../services/GenogramPerspectiveProjection';
import { resolveGenogramExportFontStack } from '../utils/exportFontStack';

const personProps = z.object({
  name: z.string().default('New Person'),
  sex: z.union([z.literal('male'), z.literal('female'), z.literal('unknown')]).default('unknown'),
  birthDate: z.string().optional(),
  birthYear: z.number().optional(),
  age: z.number().optional(),
  deathDate: z.string().optional(),
  deceased: z.boolean().optional(),
  location: z.string().optional(),
  occupation: z.string().optional(),
  education: z.union([
    z.literal('elementary'),
    z.literal('high_school'),
    z.literal('some_college'),
    z.literal('bachelors'),
    z.literal('masters'),
    z.literal('doctorate')
  ]).optional(),
  maritalStatus: z.union([
    z.literal('single'),
    z.literal('married'),
    z.literal('divorced'),
    z.literal('widowed'),
    z.literal('separated'),
    z.literal('partnered')
  ]).optional(),
  contactInfo: z.string().optional(),
  religion: z.string().optional(),
  culturalHeritageTags: z.array(z.string()).default([]).optional(),
  medicalConditionTags: z.array(z.string()).default([]).optional(),
  notes: z.string().optional(),
  photoRef: z.string().optional(),
  tags: z.array(z.string()).default([]).optional(),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  shapeType: z.string().optional(),
  // Additional shape type properties
  sexualOrientation: z.union([z.literal('gay'), z.literal('lesbian'), z.literal('bisexual'), z.literal('straight')]).optional(),
  transgender: z.boolean().optional(),
  pregnancy: z.boolean().optional(),
  stillbirth: z.boolean().optional(),
  indexPerson: z.boolean().optional(),
  institution: z.boolean().optional(),
  pet: z.boolean().optional(),
  culture: z.boolean().optional(),
  immigration: z.boolean().optional(),
});

export type PersonProps = z.infer<typeof personProps>;

function coerceBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object' && value !== null && typeof (value as any).valueOf === 'function') {
    const primitive = (value as any).valueOf();
    if (typeof primitive === 'boolean') return primitive;
    if (typeof primitive === 'number') return primitive !== 0;
    if (typeof primitive === 'string') return coerceBoolean(primitive);
  }
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === 'yes' || normalized === '1') return true;
    if (normalized === 'false' || normalized === 'no' || normalized === '0') return false;
  }
  return undefined;
}

function coerceString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function coalesceBoolean(...values: unknown[]): boolean | undefined {
  for (const value of values) {
    const coerced = coerceBoolean(value);
    if (typeof coerced === 'boolean') return coerced;
  }
  return undefined;
}

function coalesceString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const coerced = coerceString(value);
    if (typeof coerced === 'string') return coerced;
  }
  return undefined;
}

function coerceDashArray(value: unknown): number[] | undefined {
  if (Array.isArray(value)) {
    const parsed = value
      .map((entry) => {
        const num = Number(entry);
        return Number.isFinite(num) ? Math.max(0, num) : undefined;
      })
      .filter((entry): entry is number => typeof entry === 'number');
    return parsed.length ? parsed : undefined;
  }
  if (typeof value === 'string') {
    const parts = value.split(/[ ,]+/).map((segment) => Number(segment));
    const parsed = parts.filter((num) => Number.isFinite(num));
    return parsed.length ? parsed : undefined;
  }
  return undefined;
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

function coalesceNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const num = coerceNumber(value);
    if (typeof num === 'number') return num;
  }
  return undefined;
}

type RawPersonProps = PersonProps & {
  genderIdentity?: string;
  gender?: string;
  showDeceased?: unknown;
};

interface ExportPerspectiveOverlayProps {
  __exportPerspective?: 'general' | 'cultural' | 'health';
  __exportCultureMix?: ComputedCulture;
  __exportHealthMix?: ComputedHealth;
}

function renderExportCulturalOverlaySvg(
  shapeType: string,
  size: number,
  cultureMix: ComputedCulture,
  strokeWidth: number,
  clipIds: { circle: string; rect: string; diamond: string },
): string {
  if (cultureMix.length === 0) return '';
  const isFemale = shapeType.includes('female');
  const isMale = shapeType.includes('male') || shapeType === 'gay' || shapeType === 'index-person';
  const isUnknown = !isFemale && !isMale;

  if (isFemale) {
    const insetRadius = Math.max(0, size / 2 - Math.max(0, strokeWidth));
    if (cultureMix.length === 1 && cultureMix[0] && cultureMix[0].weight >= 0.999) {
      return `<g clip-path="url(#${clipIds.circle})"><circle cx="${size / 2}" cy="${size / 2}" r="${insetRadius}" fill="${cultureMix[0].color}" /></g>`;
    }
    let start = 0;
    const slices = cultureMix
      .map((item) => {
        const angle = Math.max(0, Math.min(360, item.weight * 360));
        const end = start + angle;
        const largeArc = angle > 180 ? 1 : 0;
        const x1 = size / 2 + (size / 2) * Math.cos((Math.PI / 180) * start);
        const y1 = size / 2 + (size / 2) * Math.sin((Math.PI / 180) * start);
        const x2 = size / 2 + (size / 2) * Math.cos((Math.PI / 180) * end);
        const y2 = size / 2 + (size / 2) * Math.sin((Math.PI / 180) * end);
        const path = `<path d="M ${size / 2} ${size / 2} L ${x1} ${y1} A ${size / 2} ${size / 2} 0 ${largeArc} 1 ${x2} ${y2} Z" fill="${item.color}" />`;
        start = end;
        return path;
      })
      .join('');
    return `<g clip-path="url(#${clipIds.circle})">${slices}</g>`;
  }

  let currentY = 0;
  const stacked = cultureMix
    .map((item) => {
      const height = Math.max(1, item.weight * size);
      const rect = `<rect x="0" y="${currentY}" width="${size}" height="${height}" fill="${item.color}" />`;
      currentY += height;
      return rect;
    })
    .join('');

  if (isUnknown) {
    return `<g clip-path="url(#${clipIds.diamond})">${stacked}</g>`;
  }
  return `<g clip-path="url(#${clipIds.rect})">${stacked}</g>`;
}

function renderExportHealthRingSvg(size: number, healthMix: ComputedHealth, shapeStrokeWidth: number): string {
  if (healthMix.length === 0) return '';
  const ringStrokeWidth = 4;
  const outlineInset = Math.max(1, shapeStrokeWidth);
  const gapFromOutline = 2;
  const maxOuterRadius = size / 2 - outlineInset - gapFromOutline;
  if (maxOuterRadius <= 0) return '';
  const ringRadius = Math.max(0, maxOuterRadius - ringStrokeWidth / 2);
  if (healthMix.length === 1 && healthMix[0]) {
    return `<circle cx="${size / 2}" cy="${size / 2}" r="${ringRadius}" fill="none" stroke="${healthMix[0].color}" stroke-width="${ringStrokeWidth}" />`;
  }
  const anglePer = 360 / healthMix.length;
  let start = 0;
  return healthMix
    .map((item) => {
      const end = start + anglePer;
      const largeArc = anglePer > 180 ? 1 : 0;
      const x1 = size / 2 + ringRadius * Math.cos((Math.PI / 180) * start);
      const y1 = size / 2 + ringRadius * Math.sin((Math.PI / 180) * start);
      const x2 = size / 2 + ringRadius * Math.cos((Math.PI / 180) * end);
      const y2 = size / 2 + ringRadius * Math.sin((Math.PI / 180) * end);
      const path = `<path d="M ${x1} ${y1} A ${ringRadius} ${ringRadius} 0 ${largeArc} 1 ${x2} ${y2}" fill="none" stroke="${item.color}" stroke-width="${ringStrokeWidth}" />`;
      start = end;
      return path;
    })
    .join('');
}

export function resolvePersonProps(rawProps: RawPersonProps, extras: { attrs?: Record<string, unknown> } = {}): PersonProps {
  const attrs = extras.attrs ?? {};
  const genderIdentity = coalesceString(
    rawProps.genderIdentity,
    rawProps.gender,
    attrs.genderIdentity,
    attrs.gender,
  );

  const sexualOrientation = coalesceString(
    rawProps.sexualOrientation,
    attrs.sexualOrientation,
    genderIdentity && ['gay', 'lesbian', 'bisexual', 'straight'].includes(genderIdentity)
      ? genderIdentity
      : undefined,
  ) as PersonProps['sexualOrientation'] | undefined;

  return {
    ...rawProps,
    sex: (coalesceString(rawProps.sex, attrs.sex) as PersonProps['sex']) ?? 'unknown',
    deceased:
      coalesceBoolean(rawProps.deceased, attrs.deceased, attrs.isDeceased, rawProps.showDeceased) ?? false,
    transgender:
      coalesceBoolean(rawProps.transgender, attrs.transgender, genderIdentity === 'transgender') ?? false,
    pregnancy: coalesceBoolean(rawProps.pregnancy, attrs.pregnancy),
    stillbirth: coalesceBoolean(rawProps.stillbirth, attrs.stillbirth),
    indexPerson: coalesceBoolean(rawProps.indexPerson, attrs.indexPerson, attrs.isIndexPerson),
    institution: coalesceBoolean(rawProps.institution, attrs.institution),
    pet: coalesceBoolean(rawProps.pet, attrs.pet),
    culture: coalesceBoolean(rawProps.culture,attrs.culture),
    immigration: coalesceBoolean(rawProps.immigration,attrs.immigration),
    sexualOrientation,
    shapeType: coalesceString(rawProps.shapeType, attrs.shapeType),
  };
}

// Shape type derivation function
export function deriveShapeType(props: PersonProps): string {
  const explicitShapeType = coerceString(props.shapeType);
  if (explicitShapeType) {
    return explicitShapeType;
  }
  // Check for special cases first
  if (props.institution) return 'institution';
  if (props.pet) return 'pet';
  if (props.culture) {
    return props.sex === 'female' ? 'culture-female' : 'culture-male';
  }
  if (props.immigration) {
    return props.sex === 'female' ? 'immigration-female' : 'immigration-male';
  }
  // Index person should reflect the underlying sex and deceased status
  if (props.indexPerson) {
    const baseSex: 'male' | 'female' | 'unknown' =
      props.sex === 'female' ? 'female' : props.sex === 'male' ? 'male' : 'unknown';
    if (props.deceased) {
      return (`index-person-deceased-` + baseSex) as string;
    }
    return (`index-person-` + baseSex) as string;
  }
  if (props.pregnancy) return 'pregnancy';
  if (props.stillbirth) {
    return props.sex === 'female' ? 'stillbirth-female' : 'stillbirth-male';
  }
  
  // Handle deceased cases
  if (props.deceased) {
    // Unknown sex should render as deceased-unknown (diamond with cross)
    if (props.sex === 'unknown') return 'deceased-unknown';
    if (props.transgender) {
      return props.sex === 'female' ? 'deceased-female' : 'deceased-male'; // Transgender deceased uses base sex
    }
    if (props.sexualOrientation === 'gay') {
      return props.sex === 'female' ? 'deceased-female' : 'deceased-male'; // Gay deceased uses base sex
    }
    if (props.sexualOrientation === 'lesbian') {
      return 'deceased-female';
    }
    if (props.sexualOrientation === 'bisexual') {
      return props.sex === 'female' ? 'deceased-female' : 'deceased-male';
    }
    return props.sex === 'female' ? 'deceased-female' : 'deceased-male';
  }
  
  // Handle transgender cases
  if (props.transgender) {
    return props.sex === 'female' ? 'transgender-female' : 'transgender-male';
  }
  
  // Handle sexual orientation cases
  if (props.sexualOrientation === 'gay') {
    return 'gay';
  }
  if (props.sexualOrientation === 'lesbian') {
    return 'lesbian';
  }
  if (props.sexualOrientation === 'bisexual') {
    return props.sex === 'female' ? 'bisexual-female' : 'bisexual-male';
  }
  
  // Handle pregnancy (non-deceased)
  if (props.pregnancy) {
    return 'pregnant';
  }

  // Fallback to basic sex types
  if (props.sex === 'unknown') return 'unknown';
  if (props.sex === 'female') return 'female';
  if (props.sex === 'male') return 'male';
  
  // Ultimate fallback
  return 'unknown';
}

/**
 * Get translated properties for the person manifest
 * @param t - Translation function from useTranslation('genogram')
 * @returns Translated properties
 */
export function getTranslatedPersonProperties(t: TFunction) {
  return generatePropertiesFromFieldPacks(getGenogramPersonFieldPacks(), t);
}

export const GenogramPersonManifest: ShapeManifest<PersonProps> = {
  id: 'person',
  version: '1.0.0',
  category: 'genogram',
  icon: 'user',
  propsSchema: personProps,
  defaultProps: () => ({ name: 'New Person', sex: 'unknown' }),
  defaultDimensions: { width: 100, height: 100 },
  anchors: { snap: ['top', 'right', 'bottom', 'left', 'center'], perimeter: 'rect' },
  capabilities: { selectable: true, transformable: true, textEditable: true, connectable: true },
  // Properties are dynamically generated from field packs to maintain single source of truth
  get properties() {
    return generatePropertiesFromFieldPacks(getGenogramPersonFieldPacks());
  },
  acl: () => true,
  renderers: {
    // Genogram symbol renderer with proper shapes
    svg: ({ viewModel, props }) => {
      const extendedProps = props as RawPersonProps & {
        attrs?: Record<string, unknown>;
        dash?: number[] | string;
        dashPattern?: number[] | string;
        dashEnabled?: unknown;
        strokeWidth?: number | string;
      };
      const attrs = extendedProps.attrs ?? {};
      const resolvedProps = resolvePersonProps(extendedProps, { attrs });
      const shapeType = deriveShapeType(resolvedProps);
      const vm = viewModel as { width: number; height: number; style?: Record<string, unknown> };
      const style = vm.style ?? {};
      const w = Number.isFinite(vm.width) ? vm.width : 80;
      const h = Number.isFinite(vm.height) ? vm.height : 80;
      const size = Math.max(1, Math.min(w, h));

      const dashArray = coerceDashArray(
        style.dash ?? extendedProps.dash ?? attrs.dash ?? attrs.dashPattern,
      );
      const dashEnabled =
        coalesceBoolean(
          style.dashEnabled,
          extendedProps.dashEnabled,
          attrs.dashEnabled,
          attrs.dashed,
        ) ?? false;
      // const dashAttr = dashEnabled && dashArray?.length ? ` stroke-dasharray="${dashArray.join(',')}"` : '';

      const strokeWidth =
        coalesceNumber(style.strokeWidth, extendedProps.strokeWidth, attrs.strokeWidth) ?? 2;
      const crossStrokeWidth = Math.max(1.5, strokeWidth);
      const fill =
        coalesceString(
          style.fillColor,
          style.fill,
          resolvedProps.fillColor,
          attrs.fillColor,
          typeof attrs.fill === 'string' ? attrs.fill : undefined,
        ) ?? '#f5f5f5';
      const stroke =
        coalesceString(
          style.strokeColor,
          style.stroke,
          resolvedProps.strokeColor,
          attrs.strokeColor,
          typeof attrs.stroke === 'string' ? attrs.stroke : undefined,
        ) ?? '#333';
      const textColor =
        coalesceString(
          (extendedProps as Record<string, unknown>).textColor,
          attrs.textColor,
          style.textColor,
          '#111827',
        ) ?? '#111827';
      const fontSize = coalesceNumber(
        (extendedProps as Record<string, unknown>).fontSize,
        attrs.fontSize,
        style.fontSize,
      ) ?? 12;
      const preferredFontFamily = coalesceString(
        (extendedProps as Record<string, unknown>).fontFamily,
        attrs.fontFamily,
        style.fontFamily,
      );
      const fontWeightRaw = coalesceString(
        (extendedProps as Record<string, unknown>).fontWeight,
        attrs.fontWeight,
        style.fontWeight,
      );
      const fontStyleRaw = coalesceString(
        (extendedProps as Record<string, unknown>).fontStyle,
        attrs.fontStyle,
        style.fontStyle,
      );
      const textDecoration = coalesceString(
        (extendedProps as Record<string, unknown>).textDecoration,
        attrs.textDecoration,
        style.textDecoration,
      );
      const lineHeightMultiplier = (() => {
        const lh = coalesceNumber(
          (extendedProps as Record<string, unknown>).lineHeight,
          attrs.lineHeight,
          style.lineHeight,
        );
        if (typeof lh === 'number' && Number.isFinite(lh) && lh > 0) return Math.min(3, Math.max(0.5, lh));
        return 1.2;
      })();
      const isBold = (fontWeightRaw || '').toString().toLowerCase() === 'bold' || (fontWeightRaw || '').toString() === '700' || (fontStyleRaw || '').toString().toLowerCase().includes('bold');
      const isItalic = (fontStyleRaw || '').toString().toLowerCase().includes('italic');
      const svgFontWeight = isBold ? '700' : undefined;
      const svgFontStyle = isItalic ? 'italic' : undefined;
      const text = resolvedProps.name ?? '';
      const fontFamily = resolveGenogramExportFontStack(text, preferredFontFamily);
      const metadataSource =
        ((extendedProps as { metadata?: unknown }).metadata ??
          (attrs as { metadata?: unknown }).metadata) as Record<string, unknown> | undefined;
      const metadata = typeof metadataSource === 'object' && metadataSource !== null ? metadataSource : undefined;
      const metadataPerson = metadata ? (metadata.person as Record<string, unknown> | undefined) : undefined;
      const exportLine2FieldRaw =
        coalesceString((extendedProps as any).__exportLine2Field, (attrs as any).__exportLine2Field) ?? null;

      const resolveSecondLabelText = (fieldId: string | null): string => {
        if (!fieldId) return '';
        const key = fieldId === 'participantId' ? 'researchId' : fieldId;

        const fromKey = (k: string) =>
          coalesceString(
            (resolvedProps as any)[k],
            (extendedProps as any)[k],
            (attrs as any)[k],
            metadataPerson?.[k],
            metadata?.[k],
          );

        if (fieldId === 'gender') {
          return fromKey('genderIdentity') ?? fromKey('gender') ?? '';
        }
        if (fieldId === 'educationLevel') {
          return fromKey('educationLevel') ?? fromKey('education') ?? '';
        }
        return fromKey(key) ?? '';
      };

      const secondLabelText = resolveSecondLabelText(exportLine2FieldRaw);

      const exportPerspective =
        (coalesceString(
          (extendedProps as ExportPerspectiveOverlayProps).__exportPerspective,
          (attrs as ExportPerspectiveOverlayProps).__exportPerspective,
        ) as ExportPerspectiveOverlayProps['__exportPerspective']) ?? 'general';
      const exportCultureMix =
        ((extendedProps as ExportPerspectiveOverlayProps).__exportCultureMix ??
          (attrs as ExportPerspectiveOverlayProps).__exportCultureMix) ?? [];
      const exportHealthMix =
        ((extendedProps as ExportPerspectiveOverlayProps).__exportHealthMix ??
          (attrs as ExportPerspectiveOverlayProps).__exportHealthMix) ?? [];
      const exportShapeIdRaw =
        coalesceString((extendedProps as any).__exportShapeId, (attrs as any).__exportShapeId) ?? 'shape';
      const exportShapeId = exportShapeIdRaw.replace(/[^a-zA-Z0-9_-]/g, '_');
      const clipIds = {
        circle: `geno-export-clip-circle-${exportShapeId}`,
        rect: `geno-export-clip-rect-${exportShapeId}`,
        diamond: `geno-export-clip-diamond-${exportShapeId}`,
      };

      const culturalOverlaySvg =
        exportPerspective === 'cultural' && Array.isArray(exportCultureMix) && exportCultureMix.length > 0
          ? renderExportCulturalOverlaySvg(shapeType, size, exportCultureMix, strokeWidth, clipIds)
          : '';
      const healthRingSvg =
        exportPerspective === 'health' && Array.isArray(exportHealthMix) && exportHealthMix.length > 0
          ? renderExportHealthRingSvg(size, exportHealthMix, strokeWidth)
          : '';
      const safeStrokeInset = Math.max(0, strokeWidth);
      const innerRectSize = Math.max(0, size - safeStrokeInset * 2);
      const diamondScale = Math.max(0, 1 - safeStrokeInset / Math.max(1, size / 2));
      const diamondTopX = size / 2;
      const diamondTopY = size / 2 - (size / 2) * diamondScale;
      const diamondRightX = size / 2 + (size / 2) * diamondScale;
      const diamondRightY = size / 2;
      const diamondBottomX = size / 2;
      const diamondBottomY = size / 2 + (size / 2) * diamondScale;
      const diamondLeftX = size / 2 - (size / 2) * diamondScale;
      const diamondLeftY = size / 2;
      const overlayDefs =
        culturalOverlaySvg.length > 0
          ? `<defs>
              <clipPath id="${clipIds.circle}"><circle cx="${size / 2}" cy="${size / 2}" r="${Math.max(0, size / 2 - safeStrokeInset)}" /></clipPath>
              <clipPath id="${clipIds.rect}"><rect x="${safeStrokeInset}" y="${safeStrokeInset}" width="${innerRectSize}" height="${innerRectSize}" /></clipPath>
              <clipPath id="${clipIds.diamond}"><path d="M ${diamondTopX} ${diamondTopY} L ${diamondRightX} ${diamondRightY} L ${diamondBottomX} ${diamondBottomY} L ${diamondLeftX} ${diamondLeftY} Z" /></clipPath>
            </defs>`
          : '';

      // Use shared SVG generator for consistent rendering
      const symbolSvg = generateGenogramShapeSVG(shapeType as GenogramShapeType, {
        size: Math.min(w, h), // Use the larger dimension as the base size
        strokeWidth,
        crossStrokeWidth,
        fill,
        stroke,
        textColor,
        dashArray,
        dashEnabled,
      });

      const reportHealthExportTags = (extendedProps as { reportHealthExportTags?: Array<{ tag: string; color: string }> })
        .reportHealthExportTags;
      let healthTagOverlaySvg = '';
      if (Array.isArray(reportHealthExportTags) && reportHealthExportTags.length > 0) {
        const dotR = Math.max(3, size * 0.065);
        const gap = dotR * 2.5;
        const startX = Math.max(dotR, size * 0.1);
        const cy = Math.max(dotR, size * 0.14);
        const esc = (v: string) =>
          v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        healthTagOverlaySvg = reportHealthExportTags
          .slice(0, 8)
          .map(
            (t, i) =>
              `<circle cx="${startX + i * gap}" cy="${cy}" r="${dotR}" fill="${esc(t.color)}" stroke="#333333" stroke-width="0.5"><title>${esc(t.tag)}</title></circle>`,
          )
          .join('');
      }

      // Add text label below the shape with proper spacing (matching PersonNode positioning)
      // Since the SVG content is wrapped in a transform group, text should be positioned relative to local coordinates
      // Position text like the canvas: Konva Text uses y as the top edge.
      // In SVG, we do that by using dominant-baseline="hanging".
      const nameTopY = size + 5;
      const secondDefaultTopY = size + 20;

      const normaliseLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n');
      const countLines = (value: string): number => {
        const normalized = normaliseLineEndings(value).replace(/\n+$/g, '');
        return Math.max(1, normalized.split('\n').length);
      };
      const estimateTextHeight = (lines: number, sizePx: number): number => {
        const lineHeightPx = sizePx * lineHeightMultiplier;
        const descentBuffer = Math.max(3, sizePx * 0.25);
        return sizePx + descentBuffer + Math.max(0, lines - 1) * lineHeightPx;
      };

      const rawAlign = coalesceString(
        (extendedProps as Record<string, unknown>).textAlign,
        attrs.textAlign,
        style.textAlign,
      ) ?? 'center';
      const effectiveAlign = rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left';
      const textAnchor = effectiveAlign === 'right' ? 'end' : effectiveAlign === 'center' ? 'middle' : 'start';
      const x = effectiveAlign === 'right' ? size : effectiveAlign === 'center' ? size / 2 : 0;

      const nameLine = renderMultilineText(text, {
        x,
        y: nameTopY,
        textAnchor,
        color: textColor,
        fontSize,
        fontFamily,
        fontWeight: svgFontWeight,
        fontStyle: svgFontStyle,
        textDecoration,
        lineHeightPx: fontSize * lineHeightMultiplier,
        dominantBaseline: 'hanging',
      });

      const secondLabelY = (() => {
        if (!secondLabelText.trim()) return secondDefaultTopY;
        const nameLines = countLines(text);
        const nameHeight = estimateTextHeight(nameLines, fontSize);
        const computed = nameTopY + nameHeight + 3;
        return Math.max(secondDefaultTopY, computed);
      })();

      const secondLine =
        secondLabelText.trim().length > 0
          ? renderMultilineText(secondLabelText, {
              x,
              y: secondLabelY,
              textAnchor,
              color: '#666666',
              fontSize: 10,
              fontFamily: resolveGenogramExportFontStack(secondLabelText, preferredFontFamily),
              fontWeight: 'normal',
              fontStyle: 'normal',
              textDecoration: 'none',
              lineHeightPx: 10 * lineHeightMultiplier,
              dominantBaseline: 'hanging',
            })
          : '';

      // Wrap in a group to match Konva structure
      return `<g>${overlayDefs}${symbolSvg}${culturalOverlaySvg}${healthRingSvg}${healthTagOverlaySvg}${nameLine}${secondLine}</g>`;
    },
  },
};
